import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Cookie, Settings, BarChart } from 'lucide-react';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-beige-50">
      <Header />
      <main className="pt-20">
        <article className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="glass-strong rounded-3xl p-8 md:p-12 mb-8">
              <div className="flex items-center space-x-3 mb-6">
                <Cookie className="w-10 h-10 text-brown-700" />
                <h1 className="text-4xl md:text-5xl font-bold text-brown-900">Cookie Policy</h1>
              </div>
              <p className="text-lg text-brown-700">
                Last Updated: 02.12.2025
              </p>
            </div>

            <div className="space-y-8">
              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">What Are Cookies?</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Cookies are small text files that are placed on your device when you visit a website. 
                    These files enable websites to remember your preferences, improve your browsing 
                    experience, and provide personalized content. Cookies contain information that is 
                    transferred to your device's hard drive, allowing websites to recognize you and 
                    remember your actions over time.
                  </p>
                  <p>
                    We use cookies and similar tracking technologies to collect information about how you 
                    use our website. This helps us understand visitor preferences, analyze website 
                    performance, and improve our services. By continuing to use our website, you consent 
                    to our use of cookies in accordance with this Cookie Policy.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Settings className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Types of Cookies We Use</h2>
                </div>
                <div className="space-y-6 text-brown-700 leading-relaxed">
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Essential Cookies</h3>
                    <p>
                      Essential cookies are necessary for our website to function properly. These cookies 
                      enable core functionality such as security, network management, and accessibility. 
                      They cannot be disabled in our systems as they are essential for the website to work. 
                      These cookies are typically set in response to actions you take, such as setting 
                      privacy preferences or filling in forms.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Performance and Analytics Cookies</h3>
                    <p>
                      Performance cookies help us understand how visitors interact with our website by 
                      collecting and reporting information anonymously. These cookies allow us to recognize 
                      and count the number of visitors, see how visitors move around our website, and 
                      identify which pages are most popular. This information helps us improve website 
                      functionality and user experience.
                    </p>
                    <p>
                      We use analytics services such as Google Analytics to analyze website usage patterns. 
                      These services use cookies to track visitor interactions and generate reports about 
                      website activity. The information collected is aggregated and anonymous, helping us 
                      understand overall user behavior without identifying individual users.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Functionality Cookies</h3>
                    <p>
                      Functionality cookies allow our website to remember choices you make (such as your 
                      username, language preference, or region) and provide enhanced, personalized features. 
                      These cookies may also be used to remember changes you have made to text size, fonts, 
                      and other parts of web pages that you can customize.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Targeting and Advertising Cookies</h3>
                    <p>
                      Targeting cookies are used to deliver advertisements that are relevant to you and your 
                      interests. These cookies may also be used to limit the number of times you see an 
                      advertisement and measure the effectiveness of advertising campaigns. They remember 
                      that you have visited our website and may share this information with other 
                      organizations such as advertisers.
                    </p>
                    <p>
                      We may work with third-party advertising networks that use cookies to deliver 
                      targeted advertisements on our website and other websites. These cookies help 
                      advertisers understand your browsing behavior and preferences to show you relevant 
                      ads.
                    </p>
                  </div>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <BarChart className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Third-Party Cookies</h2>
                </div>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    In addition to our own cookies, we may also use various third-party cookies to report 
                    usage statistics, deliver advertisements, and provide enhanced functionality. These 
                    third parties may set their own cookies on your device when you visit our website.
                  </p>
                  <p>
                    We use Google Analytics to analyze website traffic and user behavior. Google Analytics 
                    uses cookies to collect information about how visitors use our website, including pages 
                    visited, time spent on pages, and referring websites. This information is used to 
                    compile reports and help us improve our website. Google's privacy policy governs the 
                    use of Google Analytics cookies.
                  </p>
                  <p>
                    Third-party cookies are subject to the privacy policies of the respective third-party 
                    providers. We do not control these cookies, and we encourage you to review the privacy 
                    policies of third-party services to understand how they use cookies and what information 
                    they collect.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Cookie Duration</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Session Cookies</h3>
                    <p>
                      Session cookies are temporary cookies that are deleted when you close your browser. 
                      These cookies enable our website to recognize you during your browsing session, 
                      allowing you to move between pages without losing information. Session cookies are 
                      essential for website functionality and do not remain on your device after you close 
                      your browser.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Persistent Cookies</h3>
                    <p>
                      Persistent cookies remain on your device for a set period or until you delete them. 
                      These cookies remember your preferences and actions across multiple visits, providing 
                      a more personalized browsing experience. Persistent cookies have expiration dates 
                      that determine how long they remain on your device.
                    </p>
                    <p>
                      The duration of persistent cookies varies depending on their purpose. Some cookies 
                      may remain for a few days, while others may remain for up to two years. We set 
                      appropriate expiration periods based on the purpose of each cookie type.
                    </p>
                  </div>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Managing Your Cookie Preferences</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    You have the right to accept or reject cookies. Most web browsers automatically accept 
                    cookies, but you can usually modify your browser settings to decline cookies if you 
                    prefer. However, disabling cookies may affect the functionality of our website and 
                    prevent you from accessing certain features.
                  </p>
                  <p>
                    You can control cookies through your browser settings. Most browsers allow you to view, 
                    delete, and block cookies from specific websites. You can also configure your browser 
                    to notify you when cookies are being set, giving you the option to accept or reject 
                    them on a case-by-case basis.
                  </p>
                  <p>
                    Instructions for managing cookies vary by browser. For detailed instructions, please 
                    refer to your browser's help documentation:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Google Chrome: Settings → Privacy and Security → Cookies and other site data</li>
                    <li>Mozilla Firefox: Options → Privacy & Security → Cookies and Site Data</li>
                    <li>Microsoft Edge: Settings → Privacy, Search, and Services → Cookies and Site Permissions</li>
                    <li>Safari: Preferences → Privacy → Cookies and Website Data</li>
                  </ul>
                  <p>
                    You can also use opt-out tools provided by third-party advertising networks to manage 
                    your preferences for targeted advertising cookies. These tools allow you to opt out of 
                    receiving targeted ads from participating companies.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Impact of Disabling Cookies</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    While you can disable cookies, doing so may limit your ability to use certain features 
                    of our website. Essential cookies cannot be disabled without affecting website 
                    functionality, as they are necessary for basic operations such as security and 
                    navigation.
                  </p>
                  <p>
                    Disabling performance and analytics cookies may prevent us from understanding how our 
                    website is used, making it more difficult to improve our services. Disabling 
                    functionality cookies may prevent the website from remembering your preferences, 
                    requiring you to re-enter information or reset preferences each time you visit.
                  </p>
                  <p>
                    If you disable targeting cookies, you may still see advertisements, but they may be 
                    less relevant to your interests. We recommend allowing essential and functionality 
                    cookies to ensure optimal website performance while managing other cookie types 
                    according to your preferences.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Do Not Track Signals</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Some browsers include a "Do Not Track" (DNT) feature that sends signals to websites 
                    indicating your preference not to be tracked. Currently, there is no industry standard 
                    for responding to DNT signals, and our website does not currently respond to DNT browser 
                    signals.
                  </p>
                  <p>
                    We continue to monitor developments in DNT technology and standards. If a standard for 
                    responding to DNT signals is established in the future, we will update this Cookie Policy 
                    accordingly. In the meantime, you can manage your cookie preferences through your 
                    browser settings as described above.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Updates to This Cookie Policy</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    We may update this Cookie Policy from time to time to reflect changes in our practices, 
                    technology, legal requirements, or other factors. We will notify you of any material 
                    changes by posting the updated policy on this page with a revised "Last Updated" date.
                  </p>
                  <p>
                    Your continued use of our website after changes become effective constitutes acceptance 
                    of the revised Cookie Policy. We encourage you to review this policy periodically to 
                    stay informed about how we use cookies and similar technologies.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Contact Us</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    If you have questions about our use of cookies or this Cookie Policy, please contact us:
                  </p>
                  <div className="bg-brown-100 rounded-lg p-6">
                    <p className="font-semibold text-brown-900 mb-2">Sterling Spins</p>
                    <p>Email: info@sterling-spins.com</p>
                    <p>Phone: +44 0973 385 308</p>
                    <p>Address: London, United Kingdom</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default CookiePolicy;

